import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Users, ShoppingCart } from "lucide-react";
import Marquee from "../components/Marquee";

const HERO_IMG =
  "https://images.unsplash.com/photo-1543372654-b45dd90eeee5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Home() {
  return (
    <div style={{ backgroundColor: "#050505" }}>
      {/* ── HERO ── */}
      <section
        data-testid="hero-section"
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(5,5,5,0.72) 0%, rgba(5,5,5,0.88) 100%)",
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: "1280px",
            margin: "0 auto",
            padding: "96px 24px 80px",
          }}
        >
          <p
            className="fade-up"
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "11px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#FF3B30",
              marginBottom: "24px",
            }}
          >
            Macroeconomics Research Project — Spring 2025
          </p>
          <h1
            data-testid="research-question"
            className="fade-up fade-up-delay-1"
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontSize: "clamp(2.6rem, 6vw, 5.5rem)",
              fontWeight: 900,
              letterSpacing: "-0.03em",
              lineHeight: 1,
              color: "#FFFFFF",
              marginBottom: "32px",
              maxWidth: "950px",
            }}
          >
            How Does Government Regulation Affect Businesses, Workers, and
            Consumers in the U.S. Economy?
          </h1>
          <p
            className="fade-up fade-up-delay-2"
            style={{
              fontSize: "18px",
              color: "#A1A1AA",
              maxWidth: "600px",
              lineHeight: 1.7,
              marginBottom: "48px",
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}
          >
            A macroeconomic research project examining the real-world impact of
            U.S. regulatory policy across three groups — and whether regulation
            helps or hurts the people it's meant to serve.
          </p>
          <div
            className="fade-up fade-up-delay-3"
            style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}
          >
            <Link
              to="/background"
              data-testid="cta-start-reading"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#FF3B30",
                color: "#FFFFFF",
                padding: "14px 28px",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "none",
                transition: "opacity 0.15s ease",
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
            >
              Start Reading <ArrowRight size={15} />
            </Link>
            <Link
              to="/citations"
              data-testid="cta-view-sources"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "transparent",
                color: "#A1A1AA",
                border: "1px solid #3F3F46",
                padding: "14px 28px",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "none",
                transition: "color 0.15s ease",
              }}
              onMouseOver={(e) => (e.currentTarget.style.color = "#FFFFFF")}
              onMouseOut={(e) => (e.currentTarget.style.color = "#A1A1AA")}
            >
              View Sources
            </Link>
          </div>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <Marquee />

      {/* ── THREE IMPACT CARDS ── */}
      <section
        data-testid="impact-cards-section"
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "96px 24px",
        }}
      >
        <p
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: "11px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#52525B",
            marginBottom: "12px",
          }}
        >
          Research Focus Areas
        </p>
        <h2
          style={{
            fontFamily: "'Cabinet Grotesk', sans-serif",
            fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: "#FFFFFF",
            marginBottom: "56px",
          }}
        >
          Three Lenses of Impact
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1px",
            backgroundColor: "#27272A",
          }}
        >
          {[
            {
              icon: <Building2 size={28} />,
              accent: "#FF3B30",
              page: "03",
              label: "Impact on Businesses",
              path: "/impact-businesses",
              desc: "From compliance costs to market structure, government regulations shape how businesses operate, compete, and innovate in the U.S. economy.",
              testid: "card-businesses",
            },
            {
              icon: <Users size={28} />,
              accent: "#FFCC00",
              page: "04",
              label: "Impact on Workers",
              path: "/impact-workers",
              desc: "Minimum wage laws, workplace safety standards, and labor protections — how regulations balance worker rights with economic realities.",
              testid: "card-workers",
            },
            {
              icon: <ShoppingCart size={28} />,
              accent: "#007AFF",
              page: "05",
              label: "Impact on Consumers",
              path: "/impact-consumers",
              desc: "Drug safety, consumer financial protection, and antitrust enforcement — examining how regulations protect everyday Americans.",
              testid: "card-consumers",
            },
          ].map((card) => (
            <Link
              key={card.path}
              to={card.path}
              data-testid={card.testid}
              style={{
                display: "block",
                backgroundColor: "#121212",
                padding: "40px 36px",
                textDecoration: "none",
                transition: "background-color 0.2s ease",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#161616")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "#121212")
              }
            >
              <div style={{ color: card.accent, marginBottom: "20px" }}>
                {card.icon}
              </div>
              <p
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: "10px",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: card.accent,
                  marginBottom: "12px",
                }}
              >
                Page {card.page}
              </p>
              <h3
                style={{
                  fontFamily: "'Cabinet Grotesk', sans-serif",
                  fontSize: "22px",
                  fontWeight: 800,
                  color: "#FFFFFF",
                  marginBottom: "14px",
                  lineHeight: 1.2,
                }}
              >
                {card.label}
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#71717A",
                  lineHeight: 1.7,
                  marginBottom: "24px",
                }}
              >
                {card.desc}
              </p>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: card.accent,
                }}
              >
                Explore <ArrowRight size={13} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── ABOUT + QUICK NAV ── */}
      <section
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "0 24px 96px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "48px",
          alignItems: "start",
        }}
      >
        <div>
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "11px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#52525B",
              marginBottom: "16px",
            }}
          >
            About This Project
          </p>
          <h2
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontSize: "28px",
              fontWeight: 800,
              color: "#FFFFFF",
              marginBottom: "20px",
              lineHeight: 1.2,
            }}
          >
            Why Government Regulation?
          </h2>
          <p
            style={{
              fontSize: "15px",
              color: "#A1A1AA",
              lineHeight: 1.75,
              marginBottom: "16px",
            }}
          >
            Government regulation is one of the most debated topics in
            economics. It sits at the intersection of market efficiency, public
            safety, worker rights, and corporate freedom — and affects every
            person in the country whether they realize it or not.
          </p>
          <p
            style={{
              fontSize: "15px",
              color: "#A1A1AA",
              lineHeight: 1.75,
            }}
          >
            By examining real data, historical case studies, and economic
            research, this project attempts to answer a fundamental question:
            does government regulation truly help the people it's meant to
            serve?
          </p>
        </div>

        <div
          style={{
            border: "1px solid #27272A",
            backgroundColor: "#0D0D0D",
            padding: "36px",
          }}
        >
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "11px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#52525B",
              marginBottom: "24px",
            }}
          >
            Quick Navigation
          </p>
          {[
            { label: "01 — Background Information", path: "/background" },
            { label: "02 — Impact on Businesses", path: "/impact-businesses" },
            { label: "03 — Impact on Workers", path: "/impact-workers" },
            { label: "04 — Impact on Consumers", path: "/impact-consumers" },
            { label: "05 — MLA Citations", path: "/citations" },
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`quick-nav-${item.path.replace("/", "").replace("-", "")}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 0",
                borderBottom: "1px solid #1A1A1A",
                fontSize: "14px",
                color: "#71717A",
                textDecoration: "none",
                transition: "color 0.15s ease",
              }}
              onMouseOver={(e) => (e.currentTarget.style.color = "#FFFFFF")}
              onMouseOut={(e) => (e.currentTarget.style.color = "#71717A")}
            >
              <span>{item.label}</span>
              <ArrowRight size={13} />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
